<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <card-title-row class="pl-0 pt-5" translation-path="sidebar.attributesPainter.title">
        <template #subtitle>
          <italic-subheader
            style="font-size: 12px !important;"
            class="text-left"
            translation-path="sidebar.attributesPainter.featuresSubtitle"
          />
        </template>
      </card-title-row>
      <card-title-row
        class="pt-5"
        is-subtitle
        :straightTitle="
          `${attributesPainterObject.name}: ${attributesPainterObject.number ||
            attributesPainterObject.descAttributeValue}`
        "
      />
      <sidebar-row class="pt-3 pb-2">
        <template #actionIcon>
          <v-row no-gutters justify="center">
            <v-col cols="auto">
              <v-icon
                :color="values.find(attribute => attribute.checked) ? 'primary' : ''"
                @click="toggleAll"
                :disabled="isStickyButtonsLoading"
              >
                {{ icon }}
              </v-icon>
            </v-col>
          </v-row>
        </template>
        <v-row no-gutters>
          <v-col cols="8" class="text-left">
            {{ $i18n.t('button.selectAllItems') }}
          </v-col>
        </v-row>
      </sidebar-row>
      <template v-for="(attribute, idx) of values">
        <sidebar-row :key="idx" class="pb-2">
          <template #actionIcon>
            <v-row no-gutters justify="center">
              <v-col cols="auto">
                <v-checkbox
                  class="pl-2 ma-0"
                  v-model="attribute.checked"
                  hide-details
                  :disabled="isStickyButtonsLoading"
                />
              </v-col>
            </v-row>
          </template>
          <v-row no-gutters>
            <v-col cols="8">
              <data-input v-bind="attribute.attribute" v-model="attribute.value" :disabled="isStickyButtonsLoading" />
            </v-col>
          </v-row>
        </sidebar-row>
      </template>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'AttributesPainter',
  mixins: [prepareAttributesFeatureCardMixin],
  components: { CardTitleRow, DataInput, ItalicSubheader, MainLoader, SidebarRow },
  computed: {
    isAttributesPainterButtonActive: sync('edit/isAttributesPainterButtonActive'),
    isDataSourceUseAttributesQualifiedNames: get(
      'layers/metadata@:currentDataSource.attributes_use_datasource_qualified_names'
    ),
    selectedFeatures: get('map/selectedFeatures@:currentLayerId'),
    dataSourceIdName: get('layers/metadata@:currentDataSourceName.attributes_schema.id_name'),
    currentTab: sync('sidebar/currentTab'),
    attributesPainterObject: get('edit/attributesPainterObject'),
    currentDataSource: get('layers/metadata@:currentDataSourceName'),
    currentDataSourceName() {
      return this.attributesPainterObject['dataSource'];
    },
    currentLayer: get('layers/currentLayer'),
    currentLayerId: get('layers/currentLayer@id'),
    layersMetadata: get('layers/metadata'),
    featureId() {
      return this.attributesPainterObject?.['id'] || '';
    },
    layerAttributes() {
      return this.$_flattenStructure(
        this.getForm(
          this.currentLayer?.form_schema?.elements || [],
          this.currentDataSource?.attributes_schema?.attributes || []
        )
      );
    },
    layerAttributesEditable() {
      return this.$_flattenStructure(
        this.getForm(
          this.currentLayer?.form_schema?.elements || [],
          this.currentDataSource?.attributes_schema?.attributes || []
        )
      ).filter(attribute => attribute.editable);
    },
    allFeaturesSelected() {
      return this.values.every(value => value.checked);
    },
    someFeaturesSelected() {
      return this.values.find(value => value.checked) ? true : false;
    },
    icon() {
      if (this.allFeaturesSelected) return 'mdi-checkbox-marked';
      if (this.someFeaturesSelected) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },

    currentLayerScope: get('layers/currentLayer@layer_scope'),
    isTopologicalLayer() {
      const { currentLayerScope } = this;
      return currentLayerScope === 'water' || currentLayerScope === 'sewer';
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    isFetched: false,
    values: [],
  }),
  methods: {
    getDataSource: call('layers/getLayerMetadata'),
    calculateFieldsNonTopo: call('edit/calculateFields'),
    calculateFieldsTopo: call('edit/calculateFieldsTopo'),
    getLayerFeature: call('layers/getLayerFeature'),
    backToParent() {
      this.isAttributesPainterButtonActive = false;
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          lid: this.currentLayerId,
        },
      });
      this.currentTab = 0;
    },
    toggleAll() {
      if (this.allFeaturesSelected) {
        this.values = this.values.map(value => {
          return { ...value, checked: false };
        });
      } else {
        this.values = this.values.map(value => {
          return { ...value, checked: true };
        });
      }
    },
    getExpressions() {
      return this.values
        .filter(value => value.checked)
        .map(value => {
          return {
            FIXED: {
              [this.isDataSourceUseAttributesQualifiedNames
                ? value.attribute.attribute
                : `${this.currentDataSourceName}.${value.attribute.attribute}`]: {
                value: value.value,
              },
            },
          };
        });
    },
    getFilters() {
      return {
        $IN: {
          [this.isDataSourceUseAttributesQualifiedNames
            ? this.dataSourceIdName
            : `${this.currentDataSourceName}.${this.dataSourceIdName}`]: this.selectedFeatures,
        },
      };
    },
    runCopy() {
      const data = {
        calculator_expressions: this.getExpressions(),
        features_filter: this.getFilters(),
        dataSource: this.currentDataSourceName,
        params: {
          layer_id: this.currentLayerId,
        },
      };
      if (this.isTopologicalLayer) {
        this.topoFeaturesEditing(data);
      } else {
        this.nonTopoFeaturesEditing(data);
      }
    },
    refreshTableAndMap() {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', this.currentLayerId);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.currentLayerId] });
    },
    refreshLayersAndTable(layers) {
      this.$root.$emit('refreshTableData');
      this.$root.$emit(
        'refreshMvtSources',
        layers.map(layer => ({ id: layer, group: 'layers' })),
        true
      );
      this.$root.$emit('updateLegendStylesCounts', { layersIds: layers });
    },
    async nonTopoFeaturesEditing(data) {
      await this.editingHof(this.calculateFieldsNonTopo, data);
      this.refreshTableAndMap();
    },
    async topoFeaturesEditing(data) {
      const r = await this.editingHof(this.calculateFieldsTopo, data);
      this.refreshLayersAndTable(r);
    },
    async editingHof(fn, data) {
      try {
        this.isStickyButtonsLoading = true;
        const r = await fn(data);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditObjects'),
        });
        this.isStickyButtonsLoading = false;
        return r;
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    async init() {
      this.isFetched = false;
      try {
        const feature = JSON.parse(
          JSON.stringify(await this.getLayerFeature({ layer_id: this.currentLayerId, feature_id: this.featureId }))
        );
        const idAttributeName = this.layersMetadata[this.currentDataSourceName].attributes_schema.id_name;
        const properties = this.getFlattenProperties(feature, idAttributeName);
        const values = this.getValues(this.layerAttributes, properties);
        this.values = this.layerAttributesEditable.map(attribute => {
          return {
            attribute: {
              ...attribute,
              readonly: true,
              readOnly: true,
              hideDetails: true,
              layerId: this.currentLayerId,
              values,
            },
            value: values[attribute.attribute],
            checked: false,
          };
        });
        this.isFetched = true;
        this.isStickyButtonsVisible = true;
      } catch {
        //
      }
    },
  },
  watch: {
    someFeaturesSelected: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { someFeaturesSelected: vm.someFeaturesSelected, selectedFeatures: vm.selectedFeatures };
      },
      nV => {
        const { someFeaturesSelected, selectedFeatures } = nV;
        this.isStickyButtonsValid = someFeaturesSelected && selectedFeatures?.length ? true : false;
      }
    );
    this.stickyButtons = [
      {
        action: 'runCopy',
        translationPath: 'dialog.agreeCopy',
      },
      {
        action: 'runCancel',
        translationPath: 'dialog.disagree',
        cancel: true,
        preventGoToLegendCancel: true,
      },
    ];
    this.$root.$off('runCopy');
    this.$root.$on('runCopy', this.runCopy);
    this.$root.$off('runCancel');
    this.$root.$on('runCancel', this.backToParent);
    this.isStickyButtonsLoading = false;
    if (!this.attributesPainterObject) {
      this.backToParent();
      return;
    }
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = true;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-input--checkbox {
    .v-input__slot {
      padding-top: 7px;
      margin-top: 5px;
      margin-bottom: 0 !important;
    }
  }
}
</style>
